'use client';

import { useState, useEffect } from 'react';
import { ChevronDown, X, Bookmark, Check, Loader2 } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import * as Dialog from '@radix-ui/react-dialog';
import { useAuthStore } from '@/stores/authStore';
import { useSavedSearchStore } from '@/stores/savedSearchStore';
import VerifyEmailNudge from '@/components/auth/VerifyEmailNudge';
import {
  type SearchFilters,
  citySlugsFromFilters,
  propertyTypesFromFilters,
  sqftLabel,
} from '@/lib/searchParams';
import { cn, getPropertyTypeLabel } from '@/lib/utils';
import type { PropertyType, SortOption } from '@/types/listing';

const PRICE_PRESETS = [
  { label: 'Any', min: undefined, max: undefined },
  { label: '<$500K', min: undefined, max: 500000 },
  { label: '$500K–1M', min: 500000, max: 1000000 },
  { label: '$1M–2M', min: 1000000, max: 2000000 },
  { label: '$2M+', min: 2000000, max: undefined },
];

const BED_OPTIONS = [
  { label: 'Any', value: undefined },
  { label: '1+', value: 1 },
  { label: '2+', value: 2 },
  { label: '3+', value: 3 },
  { label: '4+', value: 4 },
  { label: '5+', value: 5 },
];

const BATH_OPTIONS = [
  { label: 'Any', value: undefined },
  { label: '1+', value: 1 },
  { label: '2+', value: 2 },
  { label: '3+', value: 3 },
  { label: '4+', value: 4 },
];

// The types we always show, even at zero, so "none right now" stays distinguishable
// from "not offered". Multi-Family is NOT among them: it matched zero of 4,194 live
// homes, so a buyer looking for a duplex picked the only option that said so and got
// a guaranteed empty page.
const CANONICAL_PROPERTY_TYPES = ['single_family', 'condo', 'townhouse'];

/**
 * The options to offer, driven by what the feed actually holds.
 *
 * A hardcoded list could neither drop a type that no longer exists nor reach one
 * that appeared: live, 39 homes sit in "other residential", "farm / ranch" and
 * "manufactured home", and no client offered any of them.
 *
 * The facet map is authoritative when present. When it is absent — a light response,
 * or a failed fetch — we fall back to the canonical three rather than showing
 * nothing, because an empty filter panel reads as a broken page.
 */
function propertyTypeOptions(facets?: Record<string, Record<string, number>>): string[] {
  const fromFeed = Object.keys(facets?.property_type ?? {});
  const union = new Set([...CANONICAL_PROPERTY_TYPES, ...fromFeed]);
  return [...union].sort((a, b) => {
    // Canonical types first, in their declared order; everything else after,
    // alphabetically, so a new subclass appears predictably rather than at random.
    const ia = CANONICAL_PROPERTY_TYPES.indexOf(a);
    const ib = CANONICAL_PROPERTY_TYPES.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
}

const SQFT_OPTIONS = [
  { label: 'Any', value: undefined },
  { label: '1,000+', value: 1000 },
  { label: '1,500+', value: 1500 },
  { label: '2,000+', value: 2000 },
  { label: '2,500+', value: 2500 },
  { label: '3,000+', value: 3000 },
];

const HOA_OPTIONS: { label: string; value: number | undefined }[] = [
  { label: 'Any', value: undefined },
  { label: 'No HOA', value: 0 },
  { label: '≤ $100', value: 100 },
  { label: '≤ $300', value: 300 },
  { label: '≤ $500', value: 500 },
];

const DOM_OPTIONS: { label: string; value: number | undefined }[] = [
  { label: 'Any', value: undefined },
  { label: 'Last 24h', value: 1 },
  { label: '3 days', value: 3 },
  { label: '1 week', value: 7 },
  { label: '2 weeks', value: 14 },
  { label: '30 days', value: 30 },
];

const YEAR_OPTIONS = [
  { label: 'Any', value: undefined },
  { label: '2020+', value: 2020 },
  { label: '2010+', value: 2010 },
  { label: '2000+', value: 2000 },
  { label: '1980+', value: 1980 },
  { label: '1960+', value: 1960 },
];

const SORT_OPTIONS: { label: string; value: SortOption }[] = [
  { label: 'Newest', value: 'newest' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
  { label: '$/sqft: Low to High', value: 'ppsf_asc' },
  { label: '$/sqft: High to Low', value: 'ppsf_desc' },
  { label: 'Beds: Most first', value: 'beds_desc' },
  { label: 'Beds: Fewest first', value: 'beds_asc' },
  { label: 'Largest', value: 'sqft' },
  { label: 'Best Match', value: 'relevance' },
  { label: 'Open house date', value: 'open_house' },
  { label: 'Biggest price cut', value: 'price_cut' },
];

const triggerClass = (active: boolean) =>
  cn(
    'filter-control flex items-center gap-2 rounded-xl border px-4 py-2 font-medium transition-colors',
    active
      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
      : 'glass text-ink-soft hover:bg-glass-bright'
  );

const popoverClass =
  'glass-blur z-[100] rounded-xl border border-hairline-strong bg-raised/95 p-2 shadow-lift-lg';

/** Survives the sign-in redirect (same tab) so Save-search resumes on return. */
const SAVE_INTENT_KEY = 'goreal:save-search-intent';

interface FilterPanelProps {
  filters: SearchFilters;
  total: number | undefined;
  onChange: (partial: Partial<SearchFilters>) => void;
  onReset: () => void;
  /** Facet distribution of the current result set (beds, property_type). */
  facets?: Record<string, Record<string, number>>;
}

/**
 * Count of results with at least `min` beds (facet keys are exact bed counts),
 * or undefined when the facet cannot honestly answer.
 *
 * `appliedMin` is the beds filter currently in force. The API computes facets
 * **after** applying it, so every bucket below it has already been dropped:
 * with `beds_min=3` on San Jose the facet is `{3:619, 4:458, 5:168, …}` and
 * summing it for the "1+" option yields **1,281** — the current total — when the
 * real answer is **1,930**. Screenshot-confirmed: the open popover read
 * "1+ 1,281  2+ 1,281  3+ 1,281", telling the buyer that widening the filter
 * would gain nothing. Options at or narrower than `appliedMin` are still exact,
 * so they keep their counts; wider ones show none, matching how the property-type
 * facet already behaves when its bucket is missing. (W12 R119)
 */
export function bedsAtLeast(
  beds: Record<string, number> | undefined,
  min: number | undefined,
  appliedMin?: number
): number | undefined {
  if (!beds || min === undefined) return undefined;
  if (appliedMin !== undefined && min < appliedMin) return undefined;
  return Object.entries(beds).reduce(
    (sum, [k, n]) => (parseInt(k, 10) >= min ? sum + n : sum),
    0
  );
}

export default function FilterPanel({ filters, total, onChange, onReset, facets }: FilterPanelProps) {
  const user = useAuthStore((state) => state.user);
  // `error` too. The save path already recorded it and nothing read it, so a
  // failed save stopped the spinner, left the dialog open and said nothing —
  // the buyer either clicked again (creating a duplicate if the retry worked) or
  // assumed it had saved.
  const { saveSearch, error: saveError } = useSavedSearchStore();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const selectedTypes = propertyTypesFromFilters(filters);

  // Save-search is auth-gated. A guest who taps it is sent to sign in and
  // returned to this exact filtered URL; an intent flag re-opens the dialog so
  // the original action completes without them re-doing the search.
  function openSaveSearch() {
    if (user) {
      setSaveDialogOpen(true);
      return;
    }
    try {
      sessionStorage.setItem(SAVE_INTENT_KEY, '1');
    } catch {
      // storage unavailable — fall through to the sign-in prompt anyway
    }
    const back = window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(back)}`;
  }

  useEffect(() => {
    if (!user) return;
    try {
      if (sessionStorage.getItem(SAVE_INTENT_KEY) === '1') {
        sessionStorage.removeItem(SAVE_INTENT_KEY);
        // Resume the auth-gated Save action now that sign-in has completed.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSaveDialogOpen(true);
      }
    } catch {
      // ignore
    }
  }, [user]);

  function setTypes(types: PropertyType[]) {
    onChange({ types: types.length ? types.join(',') : undefined });
  }

  async function handleSaveSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!searchName.trim()) return;

    setIsSaving(true);
    try {
      const filtersToSave: Record<string, unknown> = {};
      if (filters.priceMin !== undefined) filtersToSave.price_min = filters.priceMin;
      if (filters.priceMax !== undefined) filtersToSave.price_max = filters.priceMax;
      if (filters.bedsMin !== undefined) filtersToSave.beds_min = filters.bedsMin;
      if (filters.bathsMin !== undefined) filtersToSave.baths_min = filters.bathsMin;
      if (filters.sqftMin !== undefined) filtersToSave.sqft_min = filters.sqftMin;
      if (filters.sqftMax !== undefined) filtersToSave.sqft_max = filters.sqftMax;
      if (selectedTypes.length) filtersToSave.property_types = selectedTypes;
      const cities = citySlugsFromFilters(filters).map((s) => s.replace(/-/g, ' '));
      if (cities.length) filtersToSave.cities = cities;
      if (filters.yearMin !== undefined) filtersToSave.year_built_min = filters.yearMin;
      if (filters.oh) filtersToSave.open_houses_only = true;
      if (filters.cut) filtersToSave.price_cuts_only = true;
      if (filters.hoaMax !== undefined) filtersToSave.hoa_max = filters.hoaMax;
      if (filters.domMax !== undefined) filtersToSave.dom_max = filters.domMax;
      if (filters.zips) filtersToSave.zips = filters.zips.split(',');
      if (filters.bbox) filtersToSave.bbox = filters.bbox;
      if (filters.sort !== 'newest') filtersToSave.sort = filters.sort;
      if (filters.q) filtersToSave.query = filters.q;

      await saveSearch(searchName.trim(), filtersToSave, notifyEnabled);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveDialogOpen(false);
        setSaveSuccess(false);
        setSearchName('');
        setNotifyEnabled(true);
      }, 1200);
    } catch {
      // The store recorded the message; the dialog renders it below. Deliberately
      // NOT closed here — a failed save must leave the buyer's typed name in
      // place so retrying costs one click, not re-typing.
    } finally {
      setIsSaving(false);
    }
  }

  const priceActive = filters.priceMin !== undefined || filters.priceMax !== undefined;
  const activeFilterCount = [
    priceActive,
    filters.bedsMin,
    filters.bathsMin,
    filters.sqftMin,
    filters.yearMin,
    filters.oh,
    filters.cut,
    filters.hoaMax !== undefined,
    filters.domMax !== undefined,
    filters.zips,
    selectedTypes.length,
  ].filter(Boolean).length;

    // Mobile: one horizontally scrollable row (flex-nowrap + overflow-x) so the
    // filters don't stack into a tall block that buries the listings. Desktop
    // (lg): the original natural wrap. Popovers portal out, so they aren't
    // clipped by the scroll container.
  return (
    <div className="flex w-full flex-nowrap items-center gap-2 overflow-x-auto scrollbar-hide pb-1 lg:w-auto lg:flex-wrap lg:overflow-visible lg:pb-0">
      {/* Price filter */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button data-filter-control className={triggerClass(priceActive)}>
            {(() => {
              if (!priceActive) return 'Price';
              const preset = PRICE_PRESETS.find(
                (p) => p.min === filters.priceMin && p.max === filters.priceMax
              );
              return preset?.label || 'Price';
            })()}
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <div className="flex flex-wrap gap-1">
              {PRICE_PRESETS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => onChange({ priceMin: opt.min, priceMax: opt.max })}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    filters.priceMin === opt.min && filters.priceMax === opt.max
                      ? 'bg-emerald-500 text-canvas'
                      : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Beds filter */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button data-filter-control className={triggerClass(Boolean(filters.bedsMin))}>
            {filters.bedsMin ? `${filters.bedsMin}+ Beds` : 'Beds'}
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <div className="flex flex-wrap gap-1">
              {BED_OPTIONS.map((opt) => {
                const count = bedsAtLeast(facets?.beds, opt.value, filters.bedsMin);
                return (
                  <button
                    key={opt.label}
                    onClick={() => onChange({ bedsMin: opt.value })}
                    className={cn(
                      'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                      filters.bedsMin === opt.value
                        ? 'bg-emerald-500 text-canvas'
                        : 'text-ink-soft hover:bg-glass'
                    )}
                  >
                    {opt.label}
                    {count !== undefined && (
                      <span className="ml-1.5 text-xs opacity-60">{count.toLocaleString()}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Baths filter */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button data-filter-control className={triggerClass(Boolean(filters.bathsMin))}>
            {filters.bathsMin ? `${filters.bathsMin}+ Baths` : 'Baths'}
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <div className="flex flex-wrap gap-1">
              {BATH_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => onChange({ bathsMin: opt.value })}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    filters.bathsMin === opt.value
                      ? 'bg-emerald-500 text-canvas'
                      : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Property type filter */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button data-filter-control className={triggerClass(selectedTypes.length > 0)}>
            {selectedTypes.length ? `${selectedTypes.length} Types` : 'Home Type'}
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <div className="space-y-1">
              {propertyTypeOptions(facets).map((value) => (
                <label
                  key={value}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-glass"
                >
                  <input
                    type="checkbox"
                    checked={selectedTypes.includes(value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setTypes([...selectedTypes, value]);
                      } else {
                        setTypes(selectedTypes.filter((t) => t !== value));
                      }
                    }}
                    className="h-4 w-4 rounded border-hairline-strong bg-raised accent-emerald-500"
                  />
                  <span className="text-sm text-ink-soft">{getPropertyTypeLabel(value)}</span>
                  {facets?.property_type?.[value] !== undefined && (
                    <span className="ml-auto text-xs text-ink-faint">
                      {facets.property_type[value].toLocaleString()}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Sqft filter */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            data-filter-control
            className={triggerClass(
              filters.sqftMin !== undefined || filters.sqftMax !== undefined
            )}
          >
            {sqftLabel(filters) ?? 'Sqft'}
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <div className="flex flex-wrap gap-1">
              {SQFT_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => onChange({ sqftMin: opt.value })}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    filters.sqftMin === opt.value
                      ? 'bg-emerald-500 text-canvas'
                      : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Year built filter */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button data-filter-control className={triggerClass(filters.yearMin !== undefined)}>
            {filters.yearMin !== undefined ? `Built ${filters.yearMin}+` : 'Year Built'}
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <div className="flex flex-wrap gap-1">
              {YEAR_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => onChange({ yearMin: opt.value })}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    filters.yearMin === opt.value
                      ? 'bg-emerald-500 text-canvas'
                      : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Open house toggle */}
      <button
        data-filter-control
        onClick={() => onChange({ oh: filters.oh ? undefined : '1' })}
        className={triggerClass(!!filters.oh)}
        aria-pressed={!!filters.oh}
      >
        Open house
      </button>

      {/* Price cut toggle */}
      <button
        data-filter-control
        onClick={() => onChange({ cut: filters.cut ? undefined : '1' })}
        className={triggerClass(!!filters.cut)}
        aria-pressed={!!filters.cut}
      >
        Price cut
      </button>

      {/* More filters: HOA + freshness + ZIPs */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            data-filter-control
            className={triggerClass(
              filters.hoaMax !== undefined || filters.domMax !== undefined || !!filters.zips
            )}
          >
            More
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
              Max HOA fee
            </p>
            <div className="flex flex-wrap gap-1">
              {HOA_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => onChange({ hoaMax: opt.value })}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    filters.hoaMax === opt.value
                      ? 'bg-emerald-500 text-canvas'
                      : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-ink-muted">
              Listed within
            </p>
            <div className="flex flex-wrap gap-1">
              {DOM_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => onChange({ domMax: opt.value })}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                    filters.domMax === opt.value
                      ? 'bg-emerald-500 text-canvas'
                      : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-ink-muted">
              ZIP codes
            </p>
            <input
              type="text"
              inputMode="numeric"
              defaultValue={filters.zips ? filters.zips.split(',').join(', ') : ''}
              key={filters.zips ?? 'empty'}
              placeholder="95116, 95125…"
              aria-label="Filter by ZIP codes"
              onBlur={(e) => {
                const zips = e.target.value
                  .split(/[\s,]+/)
                  .filter((z) => /^\d{5}$/.test(z))
                  .slice(0, 10);
                onChange({ zips: zips.length ? zips.join(',') : undefined });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              className="w-full rounded-lg border border-hairline-strong bg-raised px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-emerald-500/60 focus:outline-none"
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Sort — reflects an active non-default sort like every other trigger */}
      <Popover.Root>
        <Popover.Trigger asChild>
          <button data-filter-control className={triggerClass(filters.sort !== 'newest')}>
            {SORT_OPTIONS.find((o) => o.value === filters.sort)?.label || 'Sort'}
            <ChevronDown className="h-4 w-4" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={popoverClass} sideOffset={8}>
            <div className="space-y-1">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onChange({ sort: opt.value })}
                  className={cn(
                    'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    filters.sort === opt.value
                      ? 'bg-emerald-500/15 font-medium text-emerald-300'
                      : 'text-ink-soft hover:bg-glass'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Clear filters */}
      {activeFilterCount > 0 && (
        <button
          onClick={onReset}
          className="flex items-center gap-1 px-3 py-2 text-sm text-ink-muted transition-colors hover:text-ink"
        >
          <X className="h-4 w-4" />
          Clear all
        </button>
      )}

      {/* Save Search — shown to everyone; guests are prompted to sign in */}
      <button
        onClick={openSaveSearch}
        className="flex flex-shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-emerald-300 transition-colors hover:bg-emerald-500/10"
      >
        <Bookmark className="h-4 w-4" />
        Save search
      </button>
      <Dialog.Root
        open={saveDialogOpen}
        onOpenChange={(open) => {
          setSaveDialogOpen(open);
          if (!open) {
            setSaveSuccess(false);
            setSearchName('');
            setNotifyEnabled(true);
          }
        }}
      >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm" />
            <Dialog.Content className="glass-blur fixed left-1/2 top-1/2 z-[201] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-card border border-hairline-strong bg-raised/95 p-6 shadow-lift-lg focus:outline-none">
              {saveSuccess ? (
                <div className="flex flex-col items-center py-4">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
                    <Check className="h-6 w-6 text-emerald-300" />
                  </div>
                  <p className="text-lg font-semibold text-ink">Search saved!</p>
                  <p className="mt-1 text-sm text-ink-muted">You can manage it in Settings.</p>
                </div>
              ) : (
                <>
                  <Dialog.Title className="font-display mb-1 text-xl text-ink">
                    Save this search
                  </Dialog.Title>
                  <Dialog.Description className="mb-5 text-sm text-ink-muted">
                    Save your current filters to quickly access them later.
                  </Dialog.Description>
                  {saveError && (
                    <p
                      role="alert"
                      className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700"
                    >
                      {saveError}
                    </p>
                  )}
                  <form onSubmit={handleSaveSearch}>
                    <div className="mb-4">
                      <label
                        htmlFor="search-name"
                        className="mb-1.5 block text-sm font-medium text-ink-soft"
                      >
                        Search name
                      </label>
                      <input
                        id="search-name"
                        type="text"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        placeholder="e.g., 3BR in Sunnyvale under $2M"
                        className="w-full rounded-xl border border-hairline bg-canvas px-3 py-2 text-ink placeholder:text-ink-faint focus:border-emerald-500/60 focus:outline-none focus:ring-2 focus:ring-emerald-500/15"
                        autoFocus
                      />
                    </div>
                    <label className="mb-6 flex cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={notifyEnabled}
                        onChange={(e) => setNotifyEnabled(e.target.checked)}
                        className="h-4 w-4 rounded border-hairline-strong bg-raised accent-emerald-500"
                      />
                      <span className="text-sm text-ink-soft">
                        Notify me when new listings match
                      </span>
                    </label>
                    <div className="-mt-3 mb-6 empty:hidden">
                      <VerifyEmailNudge compact />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Dialog.Close asChild>
                        <button
                          type="button"
                          className="rounded-xl px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-glass hover:text-ink"
                        >
                          Cancel
                        </button>
                      </Dialog.Close>
                      <button
                        type="submit"
                        disabled={isSaving || !searchName.trim()}
                        className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-canvas transition-colors hover:bg-emerald-400 disabled:opacity-50"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Saving…
                          </>
                        ) : (
                          'Save Search'
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

      {/* Results count — sticky to the right edge on mobile so it stays visible
          while the filter row scrolls under it; normal flow at lg.
          The opaque backing needs a fade on its leading edge, or a pill that
          scrolls underneath is guillotined mid-word against a hard vertical
          border and reads as a rendering glitch (seen at 390 px: "Baths" cut
          through its chevron). The gradient is suppressed from `lg`, where the
          row wraps and the count is just another static item. */}
      {total !== undefined && (
        <span className="sticky right-0 ml-auto flex shrink-0 items-center self-stretch bg-canvas pl-3 text-sm tabular-nums text-ink-muted before:absolute before:right-full before:top-0 before:h-full before:w-8 before:bg-gradient-to-l before:from-canvas before:to-transparent before:content-[''] lg:static lg:self-auto lg:bg-transparent lg:pl-0 lg:before:hidden">
          {total.toLocaleString()} homes
        </span>
      )}
    </div>
  );
}
